import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react';
import { aiAPI } from '../../services/api';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Simple badge version for campaign cards
export const TrustBadge = ({ campaignId }) => {
  const [badge, setBadge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const result = await aiAPI.getTrustBadge(campaignId);
        setBadge(result);
      } catch (err) {
        console.error('Trust badge error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBadge();
  }, [campaignId]);

  if (loading || !badge) return null;

  const colors = {
    emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
    cyan: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
    slate: 'bg-slate-500/20 text-muted-foreground border-border/50',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  };

  const icons = {
    verified: ShieldCheck,
    high_trust: Shield,
    standard: Shield,
    review_needed: ShieldAlert,
  };

  const Icon = icons[badge.level] || Shield;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge className={`${colors[badge.color]} border flex items-center gap-1`}>
            <Icon className="w-3 h-3" />
            {badge.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>Trust Score: {badge.score}/100</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Full trust score component for campaign detail page
const TrustScoreCard = ({ campaignId }) => {
  const [loading, setLoading] = useState(false);
  const [trustData, setTrustData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchTrustScore = async () => {
    setLoading(true);
    try {
      const result = await aiAPI.getTrustScore(campaignId);
      setTrustData(result);
    } catch (err) {
      console.error('Trust score error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-emerald-400';
    if (score >= 70) return 'text-emerald-400';
    if (score >= 50) return 'text-muted-foreground';
    return 'text-yellow-400';
  };

  const getLevelConfig = (level) => {
    const configs = {
      verified: { icon: ShieldCheck, color: 'emerald', label: 'Verified Campaign', bg: 'from-emerald-500/20 to-emerald-500/20' },
      high_trust: { icon: Shield, color: 'cyan', label: 'High Trust', bg: 'from-emerald-500/20 to-blue-500/20' },
      standard: { icon: Shield, color: 'slate', label: 'Standard', bg: 'from-slate-500/20 to-slate-600/20' },
      review_needed: { icon: ShieldAlert, color: 'yellow', label: 'New Campaign', bg: 'from-yellow-500/20 to-orange-500/20' },
    };
    return configs[level] || configs.standard;
  };

  if (!trustData && !loading) {
    return (
      <div className="bg-card rounded-xl p-4 border border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-500" />
            <span className="text-foreground font-medium">AI Trust Analysis</span>
          </div>
          <Button
            size="sm"
            onClick={fetchTrustScore}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            <Shield className="w-4 h-4 mr-1" /> Analyze
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">AI-powered fraud detection & trust verification</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-card rounded-xl p-4 border border-border">
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
          <span className="text-muted-foreground">Analyzing trust factors...</span>
        </div>
      </div>
    );
  }

  const config = getLevelConfig(trustData.trust_level);
  const LevelIcon = config.icon;

  return (
    <div className="bg-card/50 rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className={`p-4 bg-gradient-to-r ${config.bg}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-${config.color}-500/20`}>
              <LevelIcon className={`w-6 h-6 text-${config.color}-400`} />
            </div>
            <div>
              <p className="text-foreground font-semibold">{config.label}</p>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${getScoreColor(trustData.trust_score)}`}>
                  {trustData.trust_score}
                </span>
                <span className="text-muted-foreground text-sm">/100 Trust Score</span>
              </div>
            </div>
          </div>
        </div>
        <Progress value={trustData.trust_score} className="mt-3 h-2" />
      </div>

      {/* Verification Status */}
      <div className="p-4 border-b border-border">
        <p className="text-sm text-muted-foreground mb-3">Verification Status</p>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(trustData.verification_status).map(([key, value]) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              {value ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              ) : (
                <XCircle className="w-4 h-4 text-muted-foreground" />
              )}
              <span className={value ? 'text-white' : 'text-muted-foreground'}>
                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Expandable Details */}
      <div className="p-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center justify-between w-full text-sm"
        >
          <span className="text-muted-foreground">View Detailed Analysis</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>

        {expanded && (
          <div className="mt-4 space-y-4">
            {/* Risk Flags */}
            {trustData.risk_flags?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-yellow-400 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> Risk Flags
                </p>
                <div className="space-y-2">
                  {trustData.risk_flags.map((flag, i) => (
                    <div key={i} className={`p-2 rounded-lg ${
                      flag.severity === 'high' ? 'bg-red-500/10 border border-red-500/30' :
                      flag.severity === 'medium' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                      'bg-secondary/50'
                    }`}>
                      <p className="text-sm text-foreground">{flag.flag}</p>
                      <p className="text-xs text-muted-foreground">{flag.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trust Factors */}
            {trustData.trust_factors?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-emerald-400 mb-2 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> Trust Factors
                </p>
                <div className="space-y-2">
                  {trustData.trust_factors.map((factor, i) => (
                    <div key={i} className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-foreground">{factor.factor}</p>
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                          {factor.weight}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{factor.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendation */}
            <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-emerald-400 mt-0.5" />
                <p className="text-sm text-foreground/80">{trustData.recommendation}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Refresh */}
      <div className="px-4 pb-4">
        <Button
          size="sm"
          variant="outline"
          onClick={fetchTrustScore}
          className="w-full border-border"
        >
          <Shield className="w-4 h-4 mr-2" /> Refresh Analysis
        </Button>
      </div>
    </div>
  );
};

export default TrustScoreCard;
